import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    const base = __BASE_PATH__;
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const redirectTo = window.location.origin + cleanBase + '/reset-password';

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (resetError) {
      setError(resetError.message);
    } else {
      setSuccess(true);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-5">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-primary-100/40 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-accent-100/30 blur-[100px] pointer-events-none" />

        <div className="w-full max-w-[400px] relative z-10 text-center animate-scaleIn">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
            <i className="ri-mail-check-line text-3xl text-emerald-500" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-3">
            Email enviado!
          </h2>
          <p className="text-sm text-foreground-500 dark:text-foreground-400 mb-8 leading-relaxed">
            Enviamos um link de redefinição de senha para{' '}
            <strong className="text-foreground-700 dark:text-foreground-300">{email}</strong>.
            Verifique sua caixa de entrada e siga as instruções.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Voltar para o login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-5">
      {/* Subtle bg accents */}
      <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-primary-100/40 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-accent-100/30 blur-[100px] pointer-events-none" />

      <div className="w-full max-w-[420px] relative z-10">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <i className="ri-whatsapp-fill text-white text-xl" />
          </div>
          <span className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">WZap PRO</span>
        </div>

        <div className="mb-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
            Esqueceu a senha?
          </h2>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Digite seu email e enviaremos um link para redefinir sua senha.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
              <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1.5">
              Email
            </label>
            <div className="relative">
              <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                placeholder="seu@email.com"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md active:scale-[0.98]"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <i className="ri-send-plane-line text-lg" />
                <span>Enviar link de recuperação</span>
              </>
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-foreground-500 dark:text-foreground-400">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Voltar para o login
          </Link>
        </p>
      </div>
    </div>
  );
}